import { SPECIFICITY_WRAP } from 'client/widget-components/responsive-styles/toCSSObject';
import styled, { css } from 'styled-components';
import { customClassNames } from './utils';

const increaseClassSpecificity = (
    className: string,
    specificity: number = 2
) => {
    return `.${className}`.repeat(specificity);
};

const commonBookerContainerStyles = css`
    /* make dry run message visible inside booker */
    div.fixed:has([data-testid='dry-run-msg']) {
        display: none;
    }

    .main {
        margin-block: unset;
        height: 100%;
    }

    ${increaseClassSpecificity(customClassNames.bookerContainer)} {
        border-radius: 6px;
        width: 100% !important;
        height: 100% !important;
        isolation: isolate;
        min-height: unset !important;

        [class*='grid-area\:meta'] {
            position: static !important;
            height: 100%;
            overflow-y: auto;
        }

        [class*='grid-area\:main'] {
            overflow-y: auto;
        }

        form {
            // the way cal spacing works now: "var(--cal-spacing-4)" is now "calc(var(--spacing) * 4)"
            --cal-spacing-x2: calc(var(--spacing) * 2);
            --cal-spacing-x8: calc(var(--spacing) * 8);

            [data-testid='select-control'] {
                > div {
                    height: var(--cal-spacing-x8);
                    font-size: small;
                }

                div:has(> svg) {
                    height: auto;
                }

                input {
                    height: auto;
                }
            }

            // cal default styles
            textarea,
            // attendee address input doesn't have type explicitly set, so to refer it in css we need to do this not([type])
            input:not([type]),
            input[type='text'],
            input[type='email'],
            input[type='tel'],
            input[type='number'],
            input[type='date'],
            input[type='time'],
            input[type='datetime-local'] {
                font-size: 0.875rem;
                background-color: var(--cal-bg, #fff);
                border-color: var(--cal-border);
                border-width: 1px;
                width: 100%;
                margin-block: var(--cal-spacing-x2);
                height: var(--cal-spacing-x8);
                padding-top: var(--cal-spacing-x2);
                padding-bottom: var(--cal-spacing-x2);

                &::placeholder {
                    color: var(--cal-text, #000);
                }

                &:focus {
                    border-color: var(--brand-color);
                }
            }

            [id^='react-select-'][id$='-placeholder'] {
                color: var(--cal-text, #000);
            }

            [data-fob-field-name='guests'] {
                input {
                    background-color: transparent;
                    height: 100%;
                    margin: 0;
                    border: none;
                }

                div:has(> input):focus-within {
                    border-width: 1px;
                }
            }
        }
    }
`;

const commonEventMetaStyles = css`
    .${customClassNames.atomsWrapper}:not(.booker-skeleton,.booker-markup)
        .${customClassNames.eventMetaCustomClassNames.eventMetaContainer} {
        > div {
            display: flex;
            flex-direction: column;

            // hide empty block (placeholder)
            > div[class='h-6'] {
                display: none;
            }

            // TODO: ask cal to add more class names
            // ensure these blocks are always at the start, keep order of internal booker elements and then show pricing:
            // 0) list of avatars
            > ul {
                order: -1;
            }
            // 1) providers names
            > p {
                order: -1;
            }
            // 2) event meta title
            > .${customClassNames.eventMetaCustomClassNames.eventMetaTitle} {
                order: -1;
            }
            // 3) event meta description
            > div[data-testid='event-meta-description'] {
                order: -1;
            }
            // 4) booking pricing meta section
            > div:has(> .cal-booking-price-meta-section) {
                order: -1;
            }
            // 5) parent of selected host meta section
            div:has(> .cal-booking-selected-host-meta-section) {
                order: -1;
            }
        }
    }
`;

const themeButtonStyles = css`
    background-color: var(--btn-bg-color);
    background-image: var(--btn-bg-image);
    background-position: var(--btn-bg-position);
    background-repeat: var(--btn-bg-repeat);
    background-size: var(--btn-bg-size);
    border-color: var(--btn-border-color);
    border-bottom-color: var(--btn-border-b-color);
    border-left-color: var(--btn-border-l-color);
    border-right-color: var(--btn-border-r-color);
    border-top-color: var(--btn-border-t-color);
    border-radius: var(--btn-border-radius);
    border-bottom-left-radius: var(--btn-border-bl-radius);
    border-bottom-right-radius: var(--btn-border-br-radius);
    border-top-left-radius: var(--btn-border-tl-radius);
    border-top-right-radius: var(--btn-border-tr-radius);
    border-width: var(--btn-border-width);
    border-bottom-width: var(--btn-border-b-width);
    border-left-width: var(--btn-border-l-width);
    border-right-width: var(--btn-border-r-width);
    border-top-width: var(--btn-border-t-width);
    direction: var(--btn-text-direction);
    text-align: var(--btn-text-align);
    color: var(--btn-text-color);
    font-family: var(--btn-text-font-family);
    font-size: var(--btn-text-font-size);
    font-weight: var(--btn-text-font-weight);
    text-decoration: var(--btn-text-decoration);
    font-style: var(--btn-text-font-style);
    padding: 8px 14px;

    &:hover {
        background: var(--btn-hover-bg);
        border-color: var(--btn-hover-border-color);
        border-bottom-color: var(--btn-hover-border-b-color);
        border-left-color: var(--btn-hover-border-l-color);
        border-right-color: var(--btn-hover-border-r-color);
        border-top-color: var(--btn-hover-border-t-color);
        color: var(--btn-hover-text-color) !important;
        font-weight: var(--btn-hover-text-font-weight);
        text-decoration: var(--btn-hover-text-decoration);
        font-style: var(--btn-hover-text-font-style);
    }
`;

const themeBookerContainerStyles = css`
    .${customClassNames.bookerContainer} {
        --cal-text-color: ${({ theme }) => theme.bookerPrimaryColor};
        --cal-border-subtle: ${({ theme }) => theme.bookerPrimaryColor};
        --cal-border: ${({ theme }) => theme.bookerPrimaryColor};
        --cal-border-emphasis: ${({ theme }) => theme.bookerPrimaryColor};

        border-color: ${({ theme }) => theme.bookerPrimaryColor};
        background-color: inherit;
        color: ${({ theme }) => theme.bookerPrimaryColor};

        .react-tel-input,
        .flag-dropdown {
            background-color: inherit !important;
        }

        label {
            color: ${({ theme }) => theme.bookerPrimaryColor};
            ${({ theme }) =>
                theme.evaluateStyles({
                    styleKey: 'bookerBody',
                })}

            * {
                color: ${({ theme }) => theme.bookerPrimaryColor};
            }
        }

        [data-testid='select-control'] {
            > div {
                background-color: var(--cal-bg, #fff);
            }

            * {
                color: var(--cal-text);
            }

            input {
                border-width: 0 !important;
            }
        }

        textarea,
        input {
            background-color: ${({ theme }) => theme.bookerBackgroundColor};
            border-color: ${({ theme }) => theme.bookerPrimaryColor};
            border-width: 1px;

            &:not(:is(:focus, :hover)) {
                box-shadow: none;
            }

            &::placeholder {
                color: ${({ theme }) => theme.bookerPrimaryColor};
                opacity: 0.25;
            }
        }

        [data-fob-field-name='guests'] {
            input {
                background-color: transparent !important;
                height: 100%;
            }

            div:has(> input) {
                background-color: ${({ theme }) => theme.bookerBackgroundColor};
            }
        }
    }
`;

const themeEventMetaStyles = css`
    .${customClassNames.eventMetaCustomClassNames.eventMetaContainer} {
        svg,
        p {
            color: ${({ theme }) => theme.bookerPrimaryColor};
        }
    }
`;

const themeDatePickerStyles = css`
    .${customClassNames.datePickerCustomClassNames.datePickerContainer} {
        button {
            color: ${({ theme }) => theme.bookerPrimaryColor};
        }

        // selected date
        button[data-disabled='false']:not(
                .${customClassNames.datePickerCustomClassNames
                        .datePickerDatesActive}
            ) {
            color: ${({ theme }) => theme.bookerBackgroundColor};
            background-color: ${({ theme }) => theme.bookerPrimaryColor};
        }

        // current disabled date button
        button.bg-brand-default[data-disabled='true'] {
            color: ${({ theme }) => theme.bookerBackgroundColor};
            background-color: ${({ theme }) => theme.bookerPrimaryColor};
        }
    }

    .${customClassNames.datePickerCustomClassNames.datePickerDays} {
        color: ${({ theme }) => theme.bookerPrimaryColor};
        font-size: 0.7rem;
    }

    .${customClassNames.datePickerCustomClassNames.datePickerTitle} {
        color: ${({ theme }) => theme.bookerPrimaryColor};
        font-weight: 500;
    }

    .${customClassNames.datePickerCustomClassNames.datePickerDatesActive} {
        background-color: ${({ theme }) => theme.bookerSecondaryColor};
        color: ${({ theme }) => theme.bookerPrimaryColor};

        &:hover {
            border-color: ${({ theme }) => theme.bookerPrimaryColor};
        }
    }

    .${customClassNames.datePickerCustomClassNames.datePickerToggle} {
        color: ${({ theme }) => theme.bookerPrimaryColor};

        &:disabled {
            opacity: 0.4;
        }
    }
`;

const themeAvailableTimeSlotsStyles = css`
    .${customClassNames.availableTimeSlotsCustomClassNames
            .availableTimeSlotsHeaderContainer} {
        background-color: transparent;
    }

    .${customClassNames.availableTimeSlotsCustomClassNames
            .availableTimeSlotsTitle} {
        color: ${({ theme }) => theme.bookerPrimaryColor};
        font-weight: 500;
        font-size: 1rem;
        line-height: 1.5rem;
    }

    .${customClassNames.availableTimeSlotsCustomClassNames.availableTimes} {
        border-radius: var(--btn-border-radius);
        border-color: ${({ theme }) => theme.bookerPrimaryColor};
        background-color: transparent;
        color: ${({ theme }) => theme.bookerPrimaryColor};

        &:not(:disabled):hover {
            background-color: ${({ theme }) =>
                theme.bookerSecondaryColor} !important;
        }
    }

    .${customClassNames.availableTimeSlotsCustomClassNames
            .availableTimeSlotsTimeFormatToggle} {
        background-color: transparent;
        border-color: ${({ theme }) => theme.bookerPrimaryColor};
        button {
            color: ${({ theme }) => theme.bookerPrimaryColor};
        }
        button[aria-checked='true'] {
            color: ${({ theme }) => theme.bookerPrimaryColor};
            background-color: ${({ theme }) => theme.bookerSecondaryColor};
        }
    }
`;

const themeConfirmationStyles = css`
    button.${customClassNames.confirmStep.confirmButton} {
        ${themeButtonStyles}
    }

    button.${customClassNames.confirmStep.backButton} {
        display: none;
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        position: absolute;
        top: 0;
        left: 0;
        width: 0;
        height: 0;
        overflow: hidden;
    }

    button[data-testid='add-guests'] {
        color: ${({ theme }) => theme.bookerPrimaryColor};
        border: unset;

        &:not(:disabled):hover {
            background-color: transparent;
        }
    }
`;

export const CalBookingWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    background-color: ${({ theme }) => theme.bookerBackgroundColor};

    .booker-wrapper {
        display: flex;
        width: 100%;
        height: 100%;
    }

    .${customClassNames.atomsWrapper} {
        height: 100%;
        width: 100%;
        text-align: initial;
    }

    .booker-wrapper.booker-loading {
        .${customClassNames.atomsWrapper}:not(.booker-skeleton) {
            display: none;
        }
    }

    booker-wrapper.booker-ready {
        .${customClassNames.atomsWrapper}:not(.booker-skeleton) {
            display: block;
        }
    }

    ${commonBookerContainerStyles}
    ${commonEventMetaStyles}

    ${themeBookerContainerStyles}
    ${themeEventMetaStyles}
    ${themeDatePickerStyles}
    ${themeAvailableTimeSlotsStyles}
    ${themeConfirmationStyles}


    ${SPECIFICITY_WRAP}
    .${customClassNames.eventMetaCustomClassNames.eventMetaTitle} {
        font-size: var(--font-size-h5);
        ${({ theme }) =>
            theme.evaluateStyles({
                styleKey: 'bookerTitle',
            })}
        color: ${({ theme }) => theme.bookerPrimaryColor} !important;
    }
`;

export const containerQuery = (widgetId: string) => `
    [id="${widgetId}"] ${increaseClassSpecificity('booking-wrapper')} {
        container-type: inline-size;
        container-name: booking-wrapper;
    }

    @container booking-wrapper (max-width: 819px) {
        [id="${widgetId}"] ${increaseClassSpecificity(
    customClassNames.bookerContainer
)} {
            --booker-meta-width: 100%;
            --booker-main-width: 100%;
            --booker-timeslots-width: 100%;
            display: flex !important;
            flex-direction: column;
        }

        [id="${widgetId}"] ${increaseClassSpecificity(
    customClassNames.bookerContainer
)} > div {
            border: unset;
            height: auto !important;
            width: 100%;
        }
    }

    @container booking-wrapper (min-width: 820px) {
        [id="${widgetId}"] ${increaseClassSpecificity(
    customClassNames.bookerContainer
)} {
            --booker-meta-width: 1fr;
            --booker-main-width: 2fr;
            --booker-timeslots-width: 1fr;
            
            width: 100% !important;
        }

        [id="${widgetId}"] ${increaseClassSpecificity(
    customClassNames.bookerContainer
)}:has([class*='grid-area:timeslots']) {
            grid-template-columns: var(--booker-meta-width) var(--booker-main-width) var(--booker-timeslots-width) !important;
        }

    

        [id="${widgetId}"] ${increaseClassSpecificity(
    customClassNames.eventMetaCustomClassNames.eventMetaContainer
)} {
            min-height: 100%;
            display: flex;
            flex-direction: column;
        }
    }

    @container booking-wrapper (min-width: 1500px) {
        [id="${widgetId}"] ${increaseClassSpecificity(
    customClassNames.bookerContainer
)} {
            --booker-main-width: 750px;
        }
    }
`;
